import { ChangeDetectionStrategy, Component, Input, OnDestroy, Provider } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { EditorComponent, NGX_MONACO_EDITOR_CONFIG, NgxMonacoEditorConfig } from 'ngx-monaco-editor-v2';
import { IDisposable, editor as monacoEditor } from 'monaco-editor';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare let monaco: any;

interface MonacoEditor {
  getModel(): { id: string } | null;
}

export function getJsonValidationError(editor: MonacoEditor): ValidationErrors | null {
  const model = editor.getModel();
  if (!model) {
    return null;
  }

  const markers = monaco.editor.getModelMarkers(model.id) as monacoEditor.IMarker[];

  if (markers.length > 0) {
    return {
      monacoValidation: markers,
    };
  }

  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function provideJsonEditor(schema: any): Provider {
  function onMonacoLoad() {
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [
        {
          uri: 'schema.json',
          fileMatch: ['**'],
          schema,
        },
      ],
      trailingCommas: 'error',
      enableSchemaRequest: false,
      schemaValidation: 'error',
    });
  }

  const monacoConfig: NgxMonacoEditorConfig = {
    baseUrl: 'assets',
    onMonacoLoad,
  };

  return {
    provide: NGX_MONACO_EDITOR_CONFIG,
    useValue: monacoConfig,
  };
}

export const jsonValidator: (editor: MonacoEditor) => ValidatorFn =
  (editor: MonacoEditor) =>
  (control: AbstractControl): ValidationErrors | null => {
    try {
      if (control.value) {
        JSON.parse(control.value);
      }

      const errors = getJsonValidationError(editor);
      if (errors) {
        return errors;
      }

      return null;
    } catch (e) {
      return { invalidJson: (e as Error).message };
    }
  };

@Component({
  selector: 'dojo-json-editor',
  template: `
    <ngx-monaco-editor
      #editor
      style="height: 100%; width: 100%"
      [options]="editorOptions"
      [formControl]="formCtrl"
      (onInit)="onInit($event)"
    />
  `,
  imports: [EditorComponent, ReactiveFormsModule],
  host: {
    class: 'h-full w-full',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JsonEditorComponent implements OnDestroy {
  @Input() formCtrl!: FormControl<string>;

  editor: MonacoEditor | undefined = undefined;

  private readonly disposables: IDisposable[] = [];

  private jsonValidator: ValidatorFn | null = null;

  editorOptions = {
    automaticLayout: true,
    theme: 'vs',
    language: 'json',
    scrollBeyondLastLine: false,
    minimap: { enabled: true },
    lineHeight: 20,
    fontSize: 14,
    wordWrap: 'on',
    wrappingIndent: 'indent',
  };

  onInit(editor: MonacoEditor) {
    this.editor = editor;

    this.jsonValidator = jsonValidator(editor);
    this.formCtrl.addValidators([this.jsonValidator]);

    this.disposables.push(
      monaco.editor.onDidChangeMarkers(() => {
        this.formCtrl.updateValueAndValidity();
      }),
    );
  }

  ngOnDestroy() {
    this.disposables.forEach((disposable) => disposable.dispose());

    if (this.jsonValidator) {
      this.formCtrl.removeValidators(this.jsonValidator);
    }
  }
}
